import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";

interface TooltipProps {
  children?: React.ReactNode;
  text: React.ReactNode;
  variant?: "default" | "dark";
  side?: "top" | "bottom" | "left" | "right";
  className?: string;
  tooltipClassName?: string;
}

const Tooltip: React.FC<TooltipProps> = ({
  children,
  text,
  variant = "default",
  side = "top",
  className = "",
  tooltipClassName = "",
}) => {
  const [visible, setVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const showTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      let top = 0;
      let left = 0;

      switch (side) {
        case "bottom":
          top = rect.bottom + 8;
          left = rect.left + rect.width / 2;
          break;
        case "left":
          top = rect.top + rect.height / 2;
          left = rect.left - 8;
          break;
        case "right":
          top = rect.top + rect.height / 2;
          left = rect.right + 8;
          break;
        case "top":
        default:
          top = rect.top - 8;
          left = rect.left + rect.width / 2;
          break;
      }

      setCoords({ top, left });
    }
    setVisible(true);
  };

  const hideTooltip = () => {
    timeoutRef.current = setTimeout(() => {
      setVisible(false);
    }, 120);
  };

  // 🎨 Default icon color
  const iconColor =
    variant === "dark"
      ? "text-text hover:text-teal"
      : "text-teal hover:text-teal/80";

  return (
    <>
      <div
        ref={containerRef}
        className={`inline-block cursor-pointer ${className}`}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        {children || <Info className={`h-4 w-4 transition ${iconColor} ml-1`} />}
      </div>

      {visible &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              transform:
                side === "bottom"
                  ? "translateX(-50%)"
                  : side === "left"
                  ? "translate(-100%, -50%)"
                  : side === "right"
                  ? "translate(0, -50%)"
                  : "translate(-50%, -100%)",
            }}
            className={`z-[9999] px-3 py-2 text-sm rounded-md shadow-lg bg-white border border-gray-200 text-text whitespace-pre-line text-left min-w-max max-w-sm break-words ${tooltipClassName}`}
            onMouseEnter={() => {
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
              }
            }}
            onMouseLeave={hideTooltip}
          >
            {text}
          </div>,
          document.body
        )}
    </>
  );
};

export default Tooltip;
